import { Navigate, Route, Routes } from "react-router-dom";
// layouts
import DashboardLayout from "../layouts/dashboard";
import SimpleLayout from "../layouts/simple";
//
import ShopPage from "./ShopPage";
import UserPage from "./UserPage";
import LoginPage from "./LoginPage";
import Page404 from "./Page404";
import DashboardAppPage from "./DashboardPage";
import RegisterPage from "./RegisterPage";
import RequireAuth from "../features/auth/RequireAuth";
import PersistLogin from "../features/auth/PersistLogin";
import Prefetch from "../features/auth/Prefetch";
import useAuth from "../hooks/useAuth";
import SitesPage from "./SitesPage";
import MembersPage from "./MembersPage";
import ContractsPage from "./ContractsPage";
import BankingPage from "./BankingPage";
import TrainerPage from "./TrainerPage";
import TestingsPage from "./TestingsPage";
import TournamentsPage from "./TournamentsPage";
import EventsPage from "./EventsPage";
import ProfilePage from "./ProfilePage";
import SettingsPage from "./SettingsPage";
import SupportPage from "./SupportPage";
import SurveyPage from "./SurveyPage";
import PublicSurveyPage from "./PublicSurveyPage";
import PublicRegistrationPage from "./PublicRegistrationPage";
import PublicEventBookingPage from "./PublicEventBookingPage";
import SystemPage from "./SystemPage";
import TenantPage from "./TenantPage";
import ActivateAccountPage from "./ActivateAccountPage";
import ForgotPasswordPage from "./ForgotPasswordPage";
import ResetPasswordPage from "./ResetPasswordPage";
import MemberOverviewPage from "./MemberOverviewPage";
import MemberSitePage from "./MemberSitePage";
import MemberEventsPage from "./MemberEventsPage";
import MemberEventDetailPage from "./MemberEventDetailPage";
import MemberHomePage from "./MemberHomePage";
import TestingFeesPage from "./TestingFeesPage";
import NewsPage from "./NewsPage";
import RegistrationPlaceholderPage from "./RegistrationPlaceholderPage";
import TasksPage from "./TasksPage";
import EDojangPage from "./EDojangPage";
import FeatureGateRoute from "../components/plan/FeatureGateRoute";
import { FEATURE_KEYS } from "../utils/planFeatures";
// import RequireAuth from '../auth/components/RequireAuth';

// ----------------------------------------------------------------------

export default function Router() {
  const { isAdmin, isSuperAdmin, isMember, isUser, isEditor } = useAuth();
  const hasStaffRole = isUser || isEditor || isAdmin || isSuperAdmin;
  const isMemberOnly = isMember && !hasStaffRole;
  const canAccessMemberEvents = isMember || isUser || isEditor || isAdmin;
  const dashboardHomePath = isMemberOnly ? "/dashboard/home" : "/dashboard/app";
  const canAccessInternalRoutes = !isMemberOnly;
  const canAccessContracts = isAdmin || isSuperAdmin;

  const guardRoute = (canAccess, element) =>
    canAccess ? element : <Navigate to={dashboardHomePath} replace />;

  const guardFeatureRoute = (canAccess, featureKey, element) =>
    guardRoute(
      canAccess,
      <FeatureGateRoute
        featureKey={featureKey}
        fallbackPath={dashboardHomePath}
      >
        {element}
      </FeatureGateRoute>,
    );

  // Protected Routes
  const protectedRoutes = (
    <Route element={<PersistLogin />}>
      <Route element={<RequireAuth />}>
        <Route element={<Prefetch />}>
          <Route path="dashboard" element={<DashboardLayout />}>
            <Route
              index
              element={<Navigate to={isMemberOnly ? "home" : "app"} replace />}
            />
            <Route
              path="app"
              element={guardFeatureRoute(
                canAccessInternalRoutes,
                FEATURE_KEYS.HOME,
                <DashboardAppPage />,
              )}
            />
            <Route
              path="home"
              element={guardRoute(isMember, <MemberHomePage />)}
            />
            <Route
              path="meine-daten"
              element={guardFeatureRoute(
                isMember,
                FEATURE_KEYS.MEMBER_PORTAL,
                <MemberOverviewPage />,
              )}
            />
            <Route
              path="standorte"
              element={guardFeatureRoute(
                isMember,
                FEATURE_KEYS.MEMBER_PORTAL,
                <MemberSitePage />,
              )}
            />
            <Route
              path="standort"
              element={<Navigate to="/dashboard/standorte" replace />}
            />
            <Route
              path="overview"
              element={<Navigate to="/dashboard/meine-daten" replace />}
            />
            <Route
              path="veranstaltungen"
              element={guardFeatureRoute(
                canAccessMemberEvents,
                FEATURE_KEYS.EVENTS,
                <MemberEventsPage />,
              )}
            />
            <Route
              path="veranstaltungen/:eventId"
              element={guardFeatureRoute(
                canAccessMemberEvents,
                FEATURE_KEYS.EVENTS,
                <MemberEventDetailPage />,
              )}
            />
            <Route
              path="testing-fees"
              element={guardFeatureRoute(
                isMember,
                FEATURE_KEYS.MEMBER_PORTAL,
                <TestingFeesPage />,
              )}
            />
            <Route
              path="news/*"
              element={guardFeatureRoute(
                isMember || canAccessInternalRoutes,
                FEATURE_KEYS.NEWS,
                <NewsPage />,
              )}
            />
            <Route
              path="user"
              element={guardFeatureRoute(
                isAdmin,
                FEATURE_KEYS.USERS,
                <UserPage />,
              )}
            />
            <Route
              path="sites"
              element={guardFeatureRoute(
                canAccessInternalRoutes,
                FEATURE_KEYS.SITES,
                <SitesPage />,
              )}
            />
            <Route
              path="members/*"
              element={guardFeatureRoute(
                canAccessInternalRoutes,
                FEATURE_KEYS.MEMBERS,
                <MembersPage />,
              )}
            />
            <Route
              path="contracts"
              element={guardFeatureRoute(
                canAccessContracts,
                FEATURE_KEYS.CONTRACTS,
                <ContractsPage />,
              )}
            />
            <Route
              path="banking"
              element={guardRoute(isAdmin, <BankingPage />)}
            />
            <Route path="profil" element={guardRoute(true, <ProfilePage />)} />
            <Route path="support" element={guardRoute(true, <SupportPage />)} />
            <Route
              path="support/overview"
              element={guardRoute(isSuperAdmin, <SupportPage />)}
            />
            <Route
              path="tenants"
              element={guardRoute(isSuperAdmin, <TenantPage />)}
            />
            <Route path="settings/*" element={guardRoute(true, <SettingsPage />)} />
            <Route
              path="system"
              element={guardFeatureRoute(
                isAdmin,
                FEATURE_KEYS.SYSTEM,
                <SystemPage />,
              )}
            />
            <Route
              path="trainer"
              element={guardFeatureRoute(
                canAccessInternalRoutes,
                FEATURE_KEYS.TRAINING,
                <TrainerPage />,
              )}
            />
            <Route
              path="testings/*"
              element={guardFeatureRoute(
                canAccessInternalRoutes,
                FEATURE_KEYS.TESTINGS,
                <TestingsPage />,
              )}
            />
            <Route
              path="tournaments/*"
              element={guardFeatureRoute(
                canAccessInternalRoutes,
                FEATURE_KEYS.TOURNAMENTS,
                <TournamentsPage />,
              )}
            />
            <Route
              path="events"
              element={guardFeatureRoute(
                canAccessInternalRoutes,
                FEATURE_KEYS.EVENTS,
                <EventsPage />,
              )}
            />
            <Route
              path="tasks"
              element={guardFeatureRoute(true, FEATURE_KEYS.TASKS, <TasksPage />)}
            />
            <Route
              path="e-dojang"
              element={guardFeatureRoute(
                true,
                FEATURE_KEYS.E_DOJANG,
                <EDojangPage />,
              )}
            />
            <Route
              path="data"
              element={guardFeatureRoute(
                true,
                FEATURE_KEYS.SHOP,
                <Navigate to="/dashboard/shop" replace />,
              )}
            />
            <Route
              path="shop"
              element={guardFeatureRoute(true, FEATURE_KEYS.SHOP, <ShopPage />)}
            />
            <Route
              path="shop/produkt/:productId/:variantKey?"
              element={guardFeatureRoute(true, FEATURE_KEYS.SHOP, <ShopPage />)}
            />
            <Route
              path="survey/*"
              element={guardFeatureRoute(
                true,
                FEATURE_KEYS.SURVEY,
                <SurveyPage />,
              )}
            />
          </Route>
        </Route>
      </Route>
    </Route>
  );

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="registrierung" element={<RegistrationPlaceholderPage />} />
      {protectedRoutes}
      <Route path="survey">
        <Route path=":surveyId" element={<PublicSurveyPage />} />
      </Route>
      <Route path="registration">
        <Route path=":siteId" element={<PublicRegistrationPage />} />
      </Route>
      <Route path="public">
        <Route path="event/:eventId" element={<PublicEventBookingPage />} />
        <Route
          path="event/:eventId/registration/:token"
          element={<PublicEventBookingPage />}
        />
      </Route>
      <Route element={<SimpleLayout />}>
        <Route path="login" element={<LoginPage />} />
        <Route path="register" element={<RegisterPage />} />
        <Route path="activate/:token" element={<ActivateAccountPage />} />
        <Route path="passwort-vergessen" element={<ForgotPasswordPage />} />
        <Route path="passwort-zuruecksetzen" element={<ResetPasswordPage />} />
        <Route
          path="resetpassword"
          element={<Navigate to="/passwort-vergessen" replace />}
        />
        <Route path="404" element={<Page404 />} />
      </Route>
      <Route path="/*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}
